import { redirect } from "next/navigation";
import Link from "next/link";
import { createClient } from "@/lib/supabase/server";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Building2,
  MapPin,
  Globe,
  Users,
  Edit3,
  Star,
  ArrowLeft,
  UserPlus,
} from "lucide-react";
import { BottomNavWrapper } from "@/components/bottom-nav-wrapper";
import { AvatarUpload } from "@/components/avatar-upload";
import { RatingsSummary } from "@/components/ratings-summary";
import { RatingsList } from "@/components/ratings-list";

function toAbsoluteUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return `https://${url}`;
}

export default async function CompanyProfilePage() {
  const supabase = await createClient();

  const {
    data: { user },
  } = await supabase.auth.getUser();

  if (!user) {
    redirect("/auth/login");
  }

  // Fetch company profile
  const { data: company } = await supabase
    .from("companies")
    .select("*")
    .eq("profile_id", user.id)
    .single();

  if (!company) {
    redirect("/contractor/signup");
  }

  // Get the real total count of ratings
  const { count: totalRatings } = await supabase
    .from("ratings")
    .select("id", { count: "exact", head: true })
    .eq("to_id", user.id);

  // Fetch latest 3 ratings with profile information
  const { data: ratings } = await supabase
    .from("ratings")
    .select(`
      *,
      from_profile:profiles!ratings_from_id_fkey(
        id,
        name,
        avatar_url
      )
    `)
    .eq("to_id", user.id)
    .order("created_at", { ascending: false })
    .limit(3);

  const avgRating = ratings && ratings.length > 0
    ? ratings.reduce((sum, r) => sum + r.score, 0) / ratings.length
    : 0;

  const totalRatingsCount = totalRatings || 0;

  return (
    <div className="min-h-screen bg-background pb-20">
      <main className="mx-auto max-w-screen-sm px-4 py-6">
        <div className="mb-6 flex items-center gap-4">
          <Button asChild variant="ghost" size="icon">
            <Link href="/dashboard">
              <ArrowLeft className="h-5 w-5" />
            </Link>
          </Button>
          <h1 className="text-2xl font-bold">Company Profile</h1>
        </div>

        {/* Company header */}
        <div className="flex flex-col items-center text-center">
          <AvatarUpload
            currentUrl={company.logo_url}
            userName={company.name}
          />

          <h1 className="mt-4 text-2xl font-bold">{company.name}</h1>

          <div className="mt-2 flex flex-wrap items-center justify-center gap-4 text-sm text-muted-foreground">
            {company.address && (
              <span className="flex items-center gap-1">
                <MapPin className="h-3.5 w-3.5" />
                {company.address}
              </span>
            )}
            {company.industry && (
              <span className="flex items-center gap-1">
                <Building2 className="h-3.5 w-3.5" />
                {company.industry}
              </span>
            )}
            {avgRating > 0 && (
              <span className="flex items-center gap-1">
                ⭐ {avgRating.toFixed(1)} ({totalRatingsCount})
              </span>
            )}
          </div>

          {company.size && (
            <Badge variant="outline" className="mt-2">
              <Users className="mr-1 h-3 w-3" />
              {company.size}
            </Badge>
          )}
        </div>

        {/* Company info */}
        <div className="mt-6 space-y-3">
          {company.abn && (
            <Card>
              <CardContent className="pt-5">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                  <Building2 className="h-4 w-4" />
                  ABN
                </div>
                <p className="text-base font-mono">{company.abn}</p>
              </CardContent>
            </Card>
          )}

          {company.website && (
            <Card>
              <CardContent className="pt-5">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                  <Globe className="h-4 w-4" />
                  Website
                </div>
                <a 
                  href={toAbsoluteUrl(company.website)}
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-base text-primary hover:underline"
                >
                  {company.website}
                </a>
              </CardContent>
            </Card>
          )}

          {company.ai_research && (
            <Card>
              <CardContent className="pt-5">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-muted-foreground">
                  <Building2 className="h-4 w-4" />
                  About
                </div>
                <p className="text-base leading-relaxed">
                  {(company.ai_research as { description?: string })?.description || 
                   (company.ai_research as { summary?: string })?.summary || 
                   "Company information available."}
                </p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Ratings */}
        {(ratings && ratings.length > 0) && (
          <div className="mt-6">
            <RatingsSummary
              averageRating={avgRating}
              totalRatings={totalRatingsCount}
            />
            
            <div className="mt-4">
              <RatingsList 
                ratings={ratings}
                title="Recent Reviews"
              />
              
              {totalRatingsCount > 3 && (
                <Button asChild variant="outline" className="w-full mt-4">
                  <Link href={`/ratings/${user.id}`}>
                    <Star className="h-4 w-4 mr-2" />
                    View All Reviews ({totalRatingsCount})
                  </Link>
                </Button>
              )}
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className="mt-6 space-y-3">
          <Button asChild variant="outline" className="w-full h-12 text-base gap-2">
            <Link href="/contractor/profile/edit">
              <Edit3 className="h-5 w-5" />
              Edit Profile
            </Link>
          </Button>
          <Button asChild variant="outline" className="w-full h-12 text-base gap-2">
            <Link href="/contractor/contacts">
              <UserPlus className="h-5 w-5" />
              Manage Team Contacts
            </Link>
          </Button>
        </div>
      </main>

      <BottomNavWrapper userType="contractor" />
    </div>
  );
}